// macadamia-app/src/macadamia-core/contexts/SidebarContext.tsx
import React, { createContext, useContext, useState, useCallback } from 'react';

const DEFAULT_WIDTH = 256; // 16rem = w-64
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

interface SidebarContextType {
  width: number;
  isCollapsed: boolean;
  setWidth: (width: number) => void;
  toggleCollapse: () => void;
  expand: () => void;
  collapse: () => void;
}

const SidebarContext = createContext<SidebarContextType>({
  width: DEFAULT_WIDTH,
  isCollapsed: false,
  setWidth: () => {},
  toggleCollapse: () => {},
  expand: () => {},
  collapse: () => {},
});

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [width, setWidthState] = useState(DEFAULT_WIDTH);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const setWidth = useCallback((newWidth: number) => {
    const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);
    setWidthState(clampedWidth);
  }, []);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const expand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  return (
    <SidebarContext.Provider value={{ width, isCollapsed, setWidth, toggleCollapse, expand, collapse }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => useContext(SidebarContext);
